import React, { useState } from "react";
import { Loader2, CalendarDays, Rocket, BellRing, Plus, Pencil, Trash2, Repeat } from "lucide-react";
import { EventDialog } from "./EventDialog";
import { LaunchDialog } from "./LaunchDialog";
import api from "../../api";
import { toast } from "../../hooks/use-toast";
import { usePromoHolidays, promoFromHoliday, createPromoCode } from "./promoHolidays";

const fmt = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" });

export default function CalendarTab() {
  const [data, reload] = usePromoHolidays();
  const [autoCode, setAutoCode] = useState(true);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);
  const [launching, setLaunching] = useState(null);
  if (!data) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;
  const list = data.holidays.filter((h) => h.enabled);

  const launch = async (h, { starts_at, ends_at }) => {
    setBusy(h.id);
    try {
      const hh = { ...h, ends_at: ends_at || h.ends_at };
      if (autoCode && h.code && h.pct > 0) await createPromoCode(hh);
      await api.put("/business/me", { promo_banner: { ...promoFromHoliday(hh), ends_at, starts_at } });
      toast(starts_at
        ? { title: `${h.label} promo scheduled`, description: `Goes live ${new Date(starts_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}.` }
        : { title: `${h.label} promo is live`, description: "Your storefront banner and countdown are now showing." });
      setLaunching(null);
      reload();
    } catch (err) {
      toast({ title: "Could not launch promo", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setBusy(""); }
  };

  const turnOff = async (h) => {
    setBusy(h.id);
    try {
      await api.post("/business/promo-off");
      toast({ title: `${h.label} promo switched off`, description: "The banner has been removed from your storefront." });
      reload();
    } finally { setBusy(""); }
  };
  const removeEvent = async (h) => {
    if (!window.confirm(`Delete "${h.label}"?`)) return;
    await api.delete(`/business/events/${h.event_id}`);
    toast({ title: "Event deleted" });
    reload();
  };
  const dateLabel = (h) => (h.end_date ? `${fmt(h.date)} – ${fmt(h.end_date)}` : fmt(h.date));

  return (
    <div className="space-y-6" data-testid="calendar-tab">
      {launching && <LaunchDialog holiday={launching} busy={!!busy} onClose={() => setLaunching(null)} onLaunch={(t) => launch(launching, t)} />}
      {editing && <EventDialog event={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#16243b] flex items-center gap-2"><CalendarDays size={20} className="text-[#2ec9c9]" /> Gifting calendar</h2>
          <p className="text-sm text-[#8a97a8] mt-1 flex items-center gap-1.5"><BellRing size={14} /> We email you 14 days before each holiday. Choose regions in My Account.</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-[#16243b]">
            <input type="checkbox" checked={autoCode} onChange={(e) => setAutoCode(e.target.checked)} data-testid="calendar-auto-code" /> Also create matching discount code
          </label>
          <button onClick={() => setEditing("new")} data-testid="calendar-add-event" className="gp-btn-teal rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5"><Plus size={15} /> Add event</button>
        </div>
      </div>
      {list.length === 0 && <p className="text-[#8a97a8] text-sm" data-testid="calendar-empty">No holiday regions selected — turn some on in My Account.</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((h, i) => {
          const ref = data.promo?.ends_at || data.promo?.starts_at;
          const sameOccurrence = !ref || Math.abs(new Date(ref) - new Date(h.ends_at)) < 60 * 86400000;
          const live = data.active_preset === h.id && sameOccurrence;
          const scheduled = data.scheduled_preset === h.id && sameOccurrence;
          const fmtDT = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
          const soon = h.days_until <= 14;
          return (
            <div key={h.id} data-testid={`calendar-card-${h.id}`} style={{ animationDelay: `${i * 40}ms` }}
              className={`bg-white rounded-2xl overflow-hidden shadow-[0_8px_24px_rgba(20,30,55,0.06)] animate-[fadeIn_.4s_ease-out_both] ${soon ? "ring-2 ring-[#2ec9c9]" : ""}`}>
              <div className="px-5 py-4" style={{ background: h.bg_color, color: h.text_color }}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs opacity-80 flex items-center gap-1">{h.region_label}{h.repeat_yearly && <Repeat size={11} />}</p>
                    <p className="text-lg font-bold">{h.label}</p>
                  </div>
                  {h.custom && (
                    <span className="flex gap-2 opacity-90">
                      <button onClick={() => setEditing(h)} data-testid={`calendar-edit-${h.id}`} title="Edit"><Pencil size={15} /></button>
                      <button onClick={() => removeEvent(h)} data-testid={`calendar-delete-${h.id}`} title="Delete"><Trash2 size={15} /></button>
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-baseline justify-between">
                  <p className="text-sm text-[#5a6b80]">{dateLabel(h)}</p>
                  <p className="text-sm font-semibold text-[#16243b] shrink-0 ml-2" data-testid={`calendar-days-${h.id}`}>{h.happening ? "On now" : h.days_until === 0 ? "Today" : `${h.days_until} days`}</p>
                </div>
                <p className="text-xs text-[#8a97a8] mt-2 line-clamp-2">{h.text}</p>
                <p className="text-xs mt-2">{h.code ? <><span className="font-mono text-[#16243b]">{h.code}</span> <span className="text-[#8a97a8]">· {h.pct}% off</span></> : <span className="text-[#8a97a8]">No discount code</span>}</p>
                {live || scheduled ? (
                  <div className={`mt-4 flex items-center justify-between rounded-xl px-4 py-2.5 text-sm font-medium ${live ? "bg-[#dcfce7] text-[#166534]" : "bg-[#eef7ff] text-[#2a4a6b]"}`} data-testid={live ? `calendar-live-${h.id}` : `calendar-scheduled-${h.id}`}>
                    <span>{live ? `Live${data.promo?.ends_at ? ` · ends ${fmtDT(data.promo.ends_at)}` : ""}` : `Scheduled · starts ${fmtDT(data.promo.starts_at)}`}</span>
                    <button onClick={() => turnOff(h)} disabled={!!busy} data-testid={`calendar-turn-off-${h.id}`} title="Switch off now"
                      className="inline-flex h-6 w-11 items-center rounded-full bg-[#16a34a] transition-colors disabled:opacity-60">
                      {busy === h.id ? <Loader2 size={14} className="animate-spin text-white mx-auto" /> : <span className="h-5 w-5 rounded-full bg-white translate-x-5 transition-transform" />}
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setLaunching(h)} disabled={!!busy} data-testid={`calendar-launch-${h.id}`}
                    className="gp-btn-teal mt-4 w-full rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-60">
                    {busy === h.id ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />} Launch promo
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
